import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {
    FunctionFeature,
    ISheetAPI, ISheetContext,
    ISheetLocation,
    ISheetRange,
    ISheetRangeList, OnTimerFn,
    SheetType
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import bind from 'bind-decorator';

export class TimerFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'timer';
    }

    @Function({
        title: 'DO_EVERY(cellRange, timeInterval, shouldStart)',
        tip: 'Returns TRUE/FALSE if timer has started/stopped',
        alias: 'DO_EVERY',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet,
            FunctionFeature.UsesTimers
        ],
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'TRUE/FALSE indicating that timer has started/stopped'
            }
    })
    @bind
    public every(@Param({
                     title: 'sheetAPI',
                     tip: 'API',
                     type: SheetType.SheetAPI
                 })
                     api:ISheetAPI,
                 @Param({
                     title: 'range',
                     tip: 'Range of cells for evaluating',
                     type: SheetType.SheetRangeList
                 })
                     ranges:ISheetRangeList,
                 @Param({
                     title: 'timeInterval',
                     tip: 'A time interval in milliseconds',
                     type: Type.Number,
                     optional: true
                 })
                     timeInterval?:ANumber,
                 @Param({
                     title: 'shouldStart',
                     tip: 'Marker to start the timer',
                     type: Type.Boolean,
                     optional: true
                 })
                     shouldStart?:ABoolean):ABoolean
    {
        timeInterval = timeInterval || ANumber.fromNumber(250);
        shouldStart = shouldStart || ABoolean.true();

        const ctx = api.getSheetContext();
        const timerCtx = api.getTimerContext();

        if( !shouldStart.value )
            timerCtx.stopTimerForSlot(api.getSheetContext().getSlotId());
        else
        {
            timerCtx.registerRepeating(
                timeInterval.value,
                async () => this._executeRange(ranges, ctx),
                api.getSheetContext()
                    .getSlotId());
        }

        return shouldStart;
    }

    @Function({
        title: 'DO_AFTER(cellRange, timeInterval)',
        tip: 'Returns TRUE/FALSE if timer has started/stopped',
        alias: 'DO_AFTER',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet,
            FunctionFeature.UsesTimers
        ],
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'TRUE/FALSE indicating that timer has started/stopped'
            }
    })
    @bind
    public after(@Param({
                     title: 'sheetAPI',
                     tip: 'API',
                     type: SheetType.SheetAPI
                 })
                     api:ISheetAPI,
                 @Param({
                     title: 'range',
                     tip: 'Range of cells for evaluating',
                     type: SheetType.SheetRangeList
                 })
                     ranges:ISheetRangeList,
                 @Param({
                     title: 'timeInterval',
                     tip: 'A time interval in milliseconds',
                     type: Type.Number,
                     optional: true
                 })
                     timeInterval?:ANumber):ABoolean
    {
        timeInterval = timeInterval || ANumber.fromNumber(250);

        const ctx = api.getSheetContext();
        const timerCtx = api.getTimerContext();

        timerCtx.registerOneTime(timeInterval.value, async () => this._executeRange(ranges, ctx));

        return ABoolean.true();
    }

    private async _executeRange(ranges, ctx:ISheetContext)
    {
        for( let k = 0; k < ranges.ranges.length; k++ )
        {
            const range:ISheetRange = ranges.ranges[k];

            if( range )
            {
                const rangeIterator = ctx.getRangeIterator(range);
                const numCells = rangeIterator.numCells;

                for( let i = 0; i < numCells; i++ )
                {
                    try
                    {
                        const location:ISheetLocation = rangeIterator.getNthCellLocation(i);

                        //console.log(`RUN TIMER for location: ${location}`);
                        await ctx.reEvaluateLazyCell(location);
                    }
                    catch( e )
                    {
                        console.error(`TIMER for location ${location} => ${e}`);
                    }
                }
            }
        }
    }
}
